import React, { useContext, useState, useEffect, useMemo } from 'react';
import { Form, Field, Formik } from 'formik';
import { Environment } from 'src/Environment';

import { hasNoSpecialCharacters } from 'src/helpers/validation';
import TealiumEvents from 'src/helpers/tealium-events';
import {
  AcquiringChannelFlags,
  LockedFieldNames,
  SalesforceContext,
  Steps,
} from 'src/context/salesforce-context';
import { Checkbox, ShowIf, FormLabel } from 'src/components';
import { MiniAppFocusWrapper } from 'src/components/miniapp-focus-wrapper/miniapp-focus-wrapper';
import { CONSUMER_TYPE } from 'src/services/salesforce.service';
import { AddressInput } from '../address-input/address-input';
import { getFormStatus, merger } from 'src/features/onboarding/helpers/fields-status';
import mergeWith from 'lodash.mergewith';
import { checkAcquiringChannel } from 'src/helpers/devices';
import './your-business-address.scss';

export type Address = {
  street?: string | null;
  building?: string | null;
  suburb?: string | null;
  city?: string | null;
  province?: string | null;
  postalCode?: string | null;
};

export default function YourBusiness(props) {
  const [firstLoadEventsFired, setFirstLoadEventsFired] = useState(false);
  const { merchant, setMerchant, customerType, lead, isFieldLocked, isVodacomBusinessChannel } =
    useContext(SalesforceContext);

  const isRented = checkAcquiringChannel(
    AcquiringChannelFlags.rentOnlineFicaOnline,
    merchant?.opportunity?.deliveries,
  );

  const isCreditVettingEnabled = !Environment.current?.disabledFeatures?.creditVetting && isRented;
  useEffect(() => {
    if (
      !firstLoadEventsFired &&
      merchant?.account?.industry &&
      merchant?.account?.nature_of_business &&
      merchant?.account?.business_type
    ) {
      const assistantId = lead.dealerId || lead.agentId || '';
      TealiumEvents.businessAddressPageLoadFormStartCombined(
        merchant.account.business_type,
        customerType,
        assistantId,
        merchant.isPendingApp,
      );
      setFirstLoadEventsFired(true);
    }
  }, [merchant?.account]);

  const textTable = {
    dealer: {
      title: 'What’s the customer’s business address?',
      subtitle: 'Simply start filling the street address and we’ll do the rest.',
    },
    agent: {
      title: 'What’s the customer’s business address?',
      subtitle: 'Simply start filling the street address and we’ll do the rest.',
    },
    normal: {
      title:
        lead.consumerType === CONSUMER_TYPE.owner
          ? 'What’s your business address?'
          : 'What’s the customer’s business address?',
      subtitle: 'Simply start filling in the street address and we’ll do the rest.',
    },
    pos: {
      title: 'What’s your business address?',
      subtitle: 'Simply start filling in the street address and we’ll do the rest.',
    },
  };

  const getBusinessDetails = () => {
    const details = {
      businessAddress: {
        street: merchant?.account?.street || '',
        building: merchant?.account?.Complex_Building || '',
        suburb: merchant?.account?.suburb || '',
        city: merchant?.account?.city || '',
        province: merchant?.account?.province || merchant?.account?.region || '',
        postalCode: merchant?.account?.zip_code || '',
        autocompletedUsedOnce: false,
      },
    };

    if (isCreditVettingEnabled) {
      // @ts-ignore
      details.postalAddress = {
        street: merchant?.account?.postal_street || '',
        building: merchant?.account?.Complex_Postal_Building || '',
        suburb: merchant?.account?.suburb_postal || '',
        city: merchant?.account?.postal_city || '',
        province: merchant?.account?.postal_region || '',
        postalCode: merchant?.account?.postal_zip_code || '',
        autocompletedUsedOnce: false,
      };

      // @ts-ignore
      details.isPostalAddressSameAsBusinessAddress =
        merchant?.account?.postal_address_same_as_business_address === null
          ? false
          : merchant?.account?.postal_address_same_as_business_address;
    }

    return details;
  };

  const setBusinessDetails = (details) => {
    const businessAddressFields = {
      street: details.businessAddress.street,
      Complex_Building: details.businessAddress.building,
      suburb: details.businessAddress.suburb,
      city: details.businessAddress.city,
      region: details.businessAddress.province,
      province: details.businessAddress.province,
      country: 'South Africa',
      zip_code: details.businessAddress.postalCode,
    };

    let postalAddressFields = isCreditVettingEnabled
      ? {
          postal_street: null,
          Complex_Postal_Building: null,
          suburb_postal: null,
          postal_region: null,
          postal_city: null,
          postal_country: null,
          postal_zip_code: null,
        }
      : {};

    const miscFields = {};

    if (isCreditVettingEnabled && !details.isPostalAddressSameAsBusinessAddress) {
      postalAddressFields = {
        postal_street: details.postalAddress.street,
        Complex_Postal_Building: details.postalAddress.building,
        suburb_postal: details.postalAddress.suburb,
        postal_city: details.postalAddress.city,
        postal_region: details.postalAddress.province,
        // @ts-ignore
        postal_country: 'South Africa',
        postal_zip_code: details.postalAddress.postalCode,
      };
    }
    if (isCreditVettingEnabled && details.isPostalAddressSameAsBusinessAddress) {
      postalAddressFields = {
        postal_street: details.businessAddress.street,
        Complex_Postal_Building: details.businessAddress.building,
        suburb_postal: details.businessAddress.suburb,
        postal_city: details.businessAddress.city,
        postal_region: details.businessAddress.province,
        // @ts-ignore
        postal_country: 'South Africa',
        postal_zip_code: details.businessAddress.postalCode,
      };
    }

    if (isCreditVettingEnabled) {
      // @ts-ignore
      miscFields.postal_address_same_as_business_address =
        details.isPostalAddressSameAsBusinessAddress;
    }

    setMerchant({
      ...merchant,
      account: {
        ...merchant.account,
        ...businessAddressFields,
        ...postalAddressFields,
        ...miscFields,
      },
    });
  };

  useEffect(() => {
    props.toggleNext(isFormValid(getBusinessDetails()));
  }, [merchant]);

  useEffect(() => {
    props.toggleNext(isFormValid(getBusinessDetails()));
  }, []);

  const initialStatus = useMemo(() => {
    const defaultStatus = {
      initialDisabled: {
        businessAddress: {
          street: isFieldLocked(LockedFieldNames.ACCOUNT_STREET),
          province:
            isFieldLocked(LockedFieldNames.ACCOUNT_PROVINCE) ||
            isFieldLocked(LockedFieldNames.ACCOUNT_REGION),
          building: false,
          suburb: false,
          city: false,
          postalCode: false,
        },
      },
    };

    if (merchant.isPendingApp && !merchant?.pageUpdates) {
      throw new Error('Your business address: in pending app but no page updates available');
    }

    if (!merchant.isPendingApp) {
      return defaultStatus;
    }

    const allFields = getBusinessDetails();
    const pendingAppStatus = getFormStatus(merchant.pageUpdates, allFields, Steps.BUSINESS_ADDRESS);

    if (isVodacomBusinessChannel) {
      return mergeWith(pendingAppStatus, defaultStatus, merger);
    }

    return pendingAppStatus;
  }, [merchant?.isPendingApp, merchant?.pageUpdates]);

  return (
    <div className="YourBusinessAddress" data-testid="YourBusinessAddress">
      <div className="max-field-width">
        <div className="has-text-centered">
          <h1 className="page-title heading-four title-text">{textTable[customerType].title}</h1>
          <p className="page-title content-normal mobile-width-limit subtext">
            {textTable[customerType].subtitle}
          </p>
        </div>
        <Formik
          initialValues={getBusinessDetails()}
          initialStatus={initialStatus}
          validate={validateForm}
          validateOnChange={true}
          validateOnBlur={false}
          enableReinitialize={true}
          validateOnReset={true}
          onSubmit={() => {}}
        >
          {({ values }) => {
            return (
              <div className="business-details-container">
                <div className="business-details">
                  <Form>
                    <ShowIf condition={!isCreditVettingEnabled}>
                      <FormLabel label="Business address" className="address-section-heading">
                        <Field
                          name="businessAddress"
                          component={AddressInput}
                          prefetch={isFieldLocked(LockedFieldNames.ACCOUNT_ZIP_CODE)}
                          titleLowerCase="business address"
                          onAddressSelect={() => TealiumEvents.toolAddressUseAutofill(customerType)}
                        />
                      </FormLabel>
                    </ShowIf>

                    <ShowIf condition={isCreditVettingEnabled}>
                      <FormLabel label="Business address" className="address-section-heading">
                        <fieldset>
                          <Field
                            name="businessAddress"
                            component={AddressInput}
                            titleLowerCase="business address"
                            onAddressSelect={() =>
                              TealiumEvents.toolAddressUseAutofill(customerType)
                            }
                          />
                        </fieldset>
                      </FormLabel>

                      <FormLabel label="Postal address" className="address-section-heading">
                        <Field
                          name="isPostalAddressSameAsBusinessAddress"
                          type="checkbox"
                          component={MiniAppFocusWrapper}
                          innerComponent={Checkbox}
                          checkboxText={() => 'Postal address same as business address'}
                        />
                        {/* @ts-ignore */}
                        <ShowIf condition={!values.isPostalAddressSameAsBusinessAddress}>
                          <div className="postal-address-container">
                            <fieldset>
                              <Field
                                name="postalAddress"
                                component={AddressInput}
                                titleLowerCase="postal address"
                              />
                            </fieldset>
                          </div>
                        </ShowIf>
                      </FormLabel>
                    </ShowIf>

                    <div className="mini-spacer" />
                  </Form>
                </div>
              </div>
            );
          }}
        </Formik>
      </div>
    </div>
  );

  function isFormValid(values) {
    const errors = getErrors(values);
    return !isAnyErrors(errors);
  }

  function isFormValuesChanged(oldValues, newValues) {
    return (
      oldValues.street !== newValues.street ||
      oldValues.suburb !== newValues.suburb ||
      oldValues.building !== newValues.building ||
      oldValues.city !== newValues.city ||
      oldValues.province !== newValues.province ||
      oldValues.postalCode !== newValues.postalCode
    );
  }

  function validateAddressField(values) {
    const errors: Address = {};

    const invalidStreet = !values?.street || !hasNoSpecialCharacters(values?.street);
    errors.street = invalidStreet ? 'The street address entered is invalid' : null;

    if (!!values?.building && !hasNoSpecialCharacters(values?.building)) {
      errors.building = 'The building entered is invalid';
    }

    const invalidSuburb = !values?.suburb || !hasNoSpecialCharacters(values?.suburb);
    errors.suburb = invalidSuburb ? 'The suburb entered is invalid' : null;

    const invalidCity = !values?.city || !hasNoSpecialCharacters(values?.city);
    errors.city = invalidCity ? 'The city / town entered is invalid' : null;

    const invalidProvince = !values?.province || !values?.province;
    errors.province = invalidProvince ? 'The province entered is invalid' : null;

    const invalidPostalCode =
      !values?.postalCode ||
      values?.postalCode.length !== 4 ||
      !/^[0-9]*$/.test(values?.postalCode);
    errors.postalCode = invalidPostalCode ? 'The postal code entered is invalid' : null;

    return errors;
  }

  function getErrors(values) {
    const errors = {
      businessAddress: validateAddressField(values.businessAddress),
      postalAddress: {},
    };

    if (isCreditVettingEnabled && !values.isPostalAddressSameAsBusinessAddress) {
      errors.postalAddress = validateAddressField(values.postalAddress);
    }

    return errors;
  }

  function isAnyErrors(errors) {
    let errorsExist = false;

    Object.keys(errors.businessAddress).forEach((key) => {
      if (errors.businessAddress[key] !== null) {
        errorsExist = true;
      }
    });

    if (isCreditVettingEnabled) {
      Object.keys(errors.postalAddress).forEach((key) => {
        if (errors.postalAddress[key] !== null) {
          errorsExist = true;
        }
      });
    }

    return errorsExist;
  }

  function validateForm(values) {
    const errors = getErrors(values);
    const anyErrors = isAnyErrors(errors);
    const currentValues = getBusinessDetails();

    if (
      // @ts-ignore
      (!anyErrors && isFormValuesChanged(currentValues.businessAddress, values.businessAddress)) ||
      (isCreditVettingEnabled &&
        (values.isPostalAddressSameAsBusinessAddress !== // @ts-ignore
          currentValues.isPostalAddressSameAsBusinessAddress || // @ts-ignore
          isFormValuesChanged(currentValues.postalAddress, values.postalAddress)))
    ) {
      setBusinessDetails(values);
    }

    props.toggleNext(!anyErrors);

    return errors;
  }
}
